import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    signal,
    viewChild,
} from "@angular/core";
import { TypeManagerDecorator } from "../../../../../main/type.map.service";
import { Adjustment, InvoiceUpdateData, LineItem, LineItemType } from "../../../../../pojo/Pricing";
import { S25Assert } from "../../../../../util/s25.assert";
import { S25Util } from "../../../../../util/s25-util";
import { S25IconComponent } from "../../../../s25-icon/s25.icon.component";
import { S25PipesModule } from "../../../../../pipes/s25.pipes.module";
import { S25ModalModule } from "../../../../s25-modal/s25.modal.module";
import { s25Resource } from "../../../../../util/s25.resource";
import { S25ModalComponent } from "../../../../s25-modal/s25.modal.component";
import { RateService } from "../../../../../services/rate.service";
import { RateScheduleI } from "../../../../../pojo/RateScheduleI";
import { FormsModule } from "@angular/forms";
import { PricingStore } from "../../s25.event.pricing.store";
import { S25RadioModule } from "../../../../s25-radio/s25.radio.module";
import { FormField, form, maxLength, validate } from "@angular/forms/signals";
import { S25LoadingInlineStaticComponent } from "../../../../s25-loading/s25.loading.inline.static.component";
import { Bind } from "../../../../../decorators/bind.decorator";
import { S25ButtonComponent } from "../../../../../standalone/s25.button.component";
import { S25Const } from "../../../../../util/s25-const";
import { S25PricingUtil } from "../../../s25.pricing.util";

/** What the rate group and rate schedule dropdowns hold until the user picks something real. */
const NOTHING_CHOSEN = -999;

type AdjustmentForm = {
    rateScheduleId: number | undefined;
    rateGroupId: number | undefined;
    orgId: number | undefined;
    adjustmentValue: number;
    adjustmentType: "percent" | "amount";
    adjustmentName: string;
};

/** One line item's slice of a single occurrence, adjusted independently of the whole line item. */
export type OccurrenceAdjustmentTarget = {
    rsrvId: number;
    itemId: number;
    itemType: LineItemType;
    /** The invoice the parent line item is billed on, where a merged system set holds several. */
    billId?: number;
    profileId?: number;
    itemName?: string;
    occurrenceLabel?: string;
    chargeToId?: number;
    listPrice?: number;
    adjustmentAmt?: number;
    adjustmentPercent?: number;
    adjustmentName?: string;
};

@TypeManagerDecorator("s25-ng-pricing-adjustment-cell")
@Component({
    selector: "s25-ng-pricing-adjustment-cell",
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        S25IconComponent,
        S25PipesModule,
        S25ModalModule,
        FormsModule,
        S25RadioModule,
        FormField,
        S25LoadingInlineStaticComponent,
        S25ButtonComponent,
    ],
    template: `
        @let editing = itemEditing();
        @if (hasAdjustment(editing)) {
            <div
                [class.adj-item-wrapper]="canEdit()"
                (click)="canEdit() && openModal()"
                [attr.tabindex]="canEdit() ? 0 : null"
                [attr.role]="canEdit() ? 'button' : null"
                [attr.aria-label]="canEdit() ? 'Edit Adjustment' : null"
                (keydown.enter)="canEdit() && openModal()"
                (keydown.space)="canEdit() && openModal(); $event.preventDefault()"
            >
                @if (editing?.adjustmentName) {
                    <p class="c-margin-bottom--half">{{ editing?.adjustmentName }}</p>
                }

                @if (editing?.adjustmentPercent) {
                    <p>{{ formatPercent(editing!.adjustmentPercent!) }}</p>
                } @else if (editing?.adjustmentAmt) {
                    <p>{{ $safeNavigationMigration(editing?.adjustmentAmt) | currency }}</p>
                }
                @if (canEdit()) {
                    <span class="edit-icon">
                        <s25-ng-icon [type]="'edit'" [ariaLabel]="false" />
                    </span>
                }
            </div>
        } @else if (canEdit()) {
            <button class="aw-button aw-button--outline" (click)="openModal()">
                <s25-ng-icon [type]="'plus'" [ariaLabel]="false" />
                Add
            </button>
        } @else {
            <span class="no-adjustment">{{ 0 | currency }}</span>
        }

        <s25-ng-modal #adjustmentModal [title]="'Edit Adjustment'" [size]="'xs'">
            <ng-template #s25ModalBody>
                @if (saveSuccess()) {
                    <div class="save-success" role="status" aria-live="polite">
                        <s25-ng-icon [type]="'checkCircle'" [size]="48" [ariaLabel]="false" />
                        <span>Adjustment Saved Successfully</span>
                    </div>
                } @else if (rateSchedulesLoading()) {
                    <s25-ng-loading-inline-static />
                } @else {
                    <form>
                        @if (itemName()) {
                            <div class="adjustment-field">
                                <label for="item-name">Line Item</label>
                                <p id="item-name">{{ itemName() }}</p>
                            </div>
                        }
                        @if (occurrence()?.occurrenceLabel) {
                            <div class="adjustment-field">
                                <label for="occurrence-name">Occurrence</label>
                                <p id="occurrence-name">{{ occurrence()!.occurrenceLabel }}</p>
                            </div>
                        }
                        @if (isBillingItem()) {
                            <div class="adjustment-field">
                                <label for="rate-schedule">Rate Schedule</label>
                                <select
                                    id="rate-schedule"
                                    class="cn-form__control"
                                    [formField]="adjustmentForm.rateScheduleId"
                                >
                                    @for (option of rateScheduleOptions(); track option.itemId) {
                                        <option [value]="option.itemId">{{ option.itemName }}</option>
                                    }
                                </select>
                            </div>
                            <div class="adjustment-field">
                                <label for="rate-group">Rate Group</label>
                                <select
                                    class="cn-form__control"
                                    id="rate-group"
                                    [formField]="adjustmentForm.rateGroupId"
                                >
                                    @for (option of rateGroupOptions(); track option.itemId) {
                                        <option [value]="option.itemId">{{ option.itemName }}</option>
                                    }
                                </select>
                            </div>
                        }
                        <div class="adjustment-field">
                            <label for="org">Organization</label>
                            @if (occurrence()) {
                                <p id="org">{{ orgName() }}</p>
                            } @else {
                                <select class="cn-form__control" id="org" [formField]="adjustmentForm.orgId">
                                    @for (option of orgOptions(); track option.itemId) {
                                        <option [value]="option.itemId">{{ option.itemName }}</option>
                                    }
                                </select>
                            }
                        </div>

                        @if (isBillingItem() || occurrence()) {
                            <div class="adjustment-field">
                                <label for="price">List Price</label>
                                <p id="price">{{ listPrice() | currency }}</p>
                            </div>
                        }

                        <div class="adjustment-field">
                            <label id="adjustment-label" for="adjustment">Adjustment</label>
                            <div class="adjustment-name">
                                <input
                                    type="number"
                                    step="0.01"
                                    class="c-input"
                                    id="adjustment"
                                    [formField]="adjustmentForm.adjustmentValue"
                                />
                                @let valueField = adjustmentForm.adjustmentValue();
                                @if (valueField.invalid()) {
                                    <p class="ngRed" role="alert">{{ valueField.errors()?.[0]?.message || "" }}</p>
                                }
                                <div role="radiogroup" aria-labelledby="adjustment-label">
                                    <s25-ng-radio
                                        [formField]="adjustmentForm.adjustmentType"
                                        [radioValue]="'amount'"
                                        [radioGroupName]="'type'"
                                        >Dollar Amount ($)</s25-ng-radio
                                    >
                                    <s25-ng-radio
                                        [formField]="adjustmentForm.adjustmentType"
                                        [radioValue]="'percent'"
                                        [radioGroupName]="'type'"
                                        >Percentage (%)</s25-ng-radio
                                    >
                                </div>
                                @let reasonField = adjustmentForm.adjustmentName();
                                <label for="reason"
                                    >Reason
                                    <div class="adjustment-name-char-count" [class.char-error]="reasonField.invalid()">
                                        {{ adjustmentModel().adjustmentName.length }}&nbsp;/ 40
                                    </div>
                                </label>
                                <textarea
                                    class="cn-form__control"
                                    id="reason"
                                    [formField]="adjustmentForm.adjustmentName"
                                ></textarea>
                                @if (reasonField.invalid()) {
                                    <p class="ngRed" role="alert">{{ reasonField.errors()?.[0]?.message || "" }}</p>
                                }
                            </div>
                        </div>
                    </form>
                }
            </ng-template>
            <ng-template #s25ModalFooter>
                <div class="button-grp">
                    @if (saveSuccess()) {
                        <button class="aw-button aw-button--outline" (click)="closeAfterSuccess()">Close</button>
                    } @else if (confirmingDelete()) {
                        <p class="delete-confirm-msg">Are you sure you want to delete this adjustment?</p>
                        <s25-ng-button [type]="'danger'" [onClick]="confirmDelete">Confirm Delete</s25-ng-button>
                        <button class="aw-button aw-button--outline" (click)="cancelDelete()">Cancel</button>
                    } @else {
                        @if (saveError()) {
                            <p class="save-error" role="alert">{{ saveError() }}</p>
                        }
                        <s25-ng-button [type]="'primary'" [onClick]="save">Save</s25-ng-button>
                        @if (hasAdjustment(itemEditing())) {
                            <s25-ng-button [type]="'danger--outline'" [onClick]="promptDelete">Delete</s25-ng-button>
                        }
                    }
                </div>
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        :host {
            display: block;
        }

        .adj-item-wrapper {
            position: relative;
            cursor: pointer;
            padding: 4px;
        }

        .adj-item-wrapper:hover,
        .adj-item-wrapper:focus {
            outline: 2px solid var(--color--link-text);
            outline-offset: 2px;
            border-radius: 3px;
        }

        .edit-icon {
            position: absolute;
            bottom: 2px;
            right: 0;
            color: var(--color--link-text);
        }

        .no-adjustment {
            color: var(--color--text-muted);
        }

        form {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding-bottom: 1rem;
        }

        .adjustment-field {
            display: grid;
            align-items: center;
            justify-content: center;
            grid-template-columns: 22% 55%;
            gap: 8px;
        }

        label {
            font-weight: 600;
        }

        .adjustment-field:has(.adjustment-name) {
            align-items: start;
        }

        .adjustment-name {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        .adjustment-name label {
            display: flex;
            gap: 8px;
            justify-content: space-between;
        }

        s25-ng-loading-inline-static {
            display: block;
            width: fit-content;
            margin: auto;
        }

        .adjustment-name-char-count {
            color: var(--color--text-success);
        }

        .adjustment-name-char-count.char-error {
            color: var(--color--text-danger);
        }

        .ngRed {
            font-size: 0.9rem;
        }

        .save-success {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 12px;
            padding: 2rem 1rem;
            color: var(--color--text-success);
        }

        .save-error {
            color: var(--color--text-danger);
            margin-bottom: 8px;
            font-size: 0.9rem;
        }

        .delete-confirm-msg {
            color: var(--color--text-danger);
            flex: 1;
            font-weight: 600;
            flex-basis: 100%;
        }

        .button-grp {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;
            gap: 1rem;
        }

        @media (max-width: 640px) {
            .adjustment-field {
                grid-template-columns: 1fr;
            }

            .button-grp {
                flex-direction: column;
            }

            .button-grp s25-ng-button {
                flex: 1;
            }
        }
    `,
})
export class S25PricingAdjustmentCellComponent {
    readonly lineItem = input<LineItem | undefined>(undefined);
    readonly adjustment = input<Adjustment | undefined>(undefined);
    readonly occurrence = input<OccurrenceAdjustmentTarget | undefined>(undefined);
    readonly orgId = input<number | undefined>(undefined);
    /** Set where the amounts only report what is billed, as the invoice summary does. */
    readonly readOnly = input(false);

    readonly isBillingItem = computed(() => !!this.lineItem());

    /** What this cell adjusts, and so which array a save has to carry. */
    private readonly adjustmentTarget = computed<"lineItem" | "occurrence" | "org">(() => {
        if (this.isBillingItem()) return "lineItem";
        if (this.occurrence()) return "occurrence";
        return "org";
    });

    readonly itemEditing = computed<LineItem | Adjustment | OccurrenceAdjustmentTarget | undefined>(
        () => this.lineItem() ?? this.adjustment() ?? this.occurrence(),
    );

    protected readonly itemName = computed(() => this.lineItem()?.itemName ?? this.occurrence()?.itemName);
    protected readonly listPrice = computed(() => this.lineItem()?.listPrice ?? this.occurrence()?.listPrice ?? 0);

    readonly modal = viewChild("adjustmentModal", { read: S25ModalComponent });

    private readonly pricingStore = inject(PricingStore);

    /**
     * The invoice this cell writes to. A merged system set holds rows billed on several, each
     * stamped with its own, so a row answers for itself; a new org adjustment has no row to ask and
     * lands on the invoice the tab names.
     */
    readonly invoiceId = computed(
        () =>
            this.lineItem()?.billId ??
            this.occurrence()?.billId ??
            this.adjustment()?.billId ??
            this.pricingStore.evBillId(),
    );

    readonly canEdit = computed(
        () =>
            !this.readOnly() &&
            this.pricingStore.canEditPricing() &&
            !this.pricingStore.invoiceById().get(this.invoiceId()!)?.billDefn?.completed,
    );

    readonly saveSuccess = signal(false);
    readonly saveError = signal("");
    readonly confirmingDelete = signal(false);

    protected readonly formatPercent = S25PricingUtil.formatPercent;

    protected readonly orgName = computed(() => {
        const chargeToId = this.itemEditing()?.chargeToId ?? this.orgId();
        return this.pricingStore.organizations().find((org) => Number(org.itemId) === chargeToId)?.itemName ?? "";
    });

    hasAdjustment(item: LineItem | Adjustment | OccurrenceAdjustmentTarget | undefined) {
        if (!item) return false;
        const { adjustmentAmt, adjustmentPercent } = item;
        return S25Util.isDefined(adjustmentAmt) || S25Util.isDefined(adjustmentPercent);
    }

    private readonly rateSchedulesResp = s25Resource({
        if: () => !!this.modal()?.isOpen() && !!this.lineItem(),
        params: () => ({ modalOpen: this.modal()?.isOpen(), lineItem: this.lineItem() }),
        loader: ({ params: { lineItem } }) => RateService.getRateSchedules(lineItem!.itemType, lineItem!.itemId),
    });

    protected readonly rateSchedulesLoading = this.rateSchedulesResp.resource.isLoading;

    protected readonly rateGroupOptions = computed(() => [
        { itemId: NOTHING_CHOSEN, itemName: "Choose a Rate Group" },
        ...this.pricingStore.allRateGroups().filter((group) => group.isActive),
    ]);
    protected orgOptions = this.pricingStore.organizations;
    protected readonly rateScheduleOptions = computed(() => [
        { itemId: NOTHING_CHOSEN, itemName: "Choose a Rate Schedule" },
        ...(this.rateSchedulesResp
            .value()
            ?.map(({ rate_name, rate_id }: RateScheduleI) => ({ itemName: rate_name, itemId: rate_id })) ?? []),
    ]);

    adjustmentModel = linkedSignal<AdjustmentForm>(() => ({
        rateScheduleId: this.lineItem()?.rateId ?? NOTHING_CHOSEN,
        rateGroupId: this.lineItem()?.rateGroupId ?? NOTHING_CHOSEN,
        orgId: this.itemEditing()?.chargeToId ?? this.orgId() ?? NOTHING_CHOSEN,
        adjustmentValue: this.itemEditing()?.adjustmentPercent ?? this.itemEditing()?.adjustmentAmt ?? 0,
        adjustmentType: S25Util.isDefined(this.itemEditing()?.adjustmentPercent) ? "percent" : "amount",
        adjustmentName: this.itemEditing()?.adjustmentName ?? "",
    }));

    readonly adjustmentForm = form(this.adjustmentModel, (schema) => {
        maxLength(schema.adjustmentName, 40, { message: "Reason cannot exceed 40 characters" });
        validate(schema.adjustmentValue, ({ value, valueOf }) => {
            if (valueOf(schema.adjustmentType) === "percent" && (value() > 999 || value() < -999)) {
                return { kind: "adjustmentValue", message: "Please enter a percentage between 999 and -999" };
            } else if (value() > 999_999_999_999 || value() < -999_999_999_999) {
                return {
                    kind: "adjustmentValue",
                    message: "Please enter an amount between 999,999,999,999 and -999,999,999,999",
                };
            }
        });
    });

    private applyMerge: (() => void) | undefined = undefined;

    openModal() {
        this.saveSuccess.set(false);
        this.confirmingDelete.set(false);
        this.modal()?.open();
    }

    closeAfterSuccess() {
        this.modal()?.close();
        this.applyMerge?.();
        this.applyMerge = undefined;
    }

    @Bind
    async save() {
        if (this.adjustmentForm().invalid() || S25Util.isUndefined(this.invoiceId())) return;
        this.saveError.set("");

        const { rateScheduleId, rateGroupId, orgId, adjustmentType, adjustmentValue, adjustmentName } =
            this.adjustmentModel();

        const updateData = this.buildUpdateData({
            orgId,
            rateScheduleId,
            rateGroupId,
            adjustmentPct: adjustmentType === "percent" ? S25PricingUtil.roundPercent(adjustmentValue) : undefined,
            adjustmentAmt: adjustmentType === "amount" ? adjustmentValue : undefined,
            reason: S25Util.isDefined(adjustmentValue) && adjustmentValue !== 0 ? adjustmentName : "",
        });

        const { error, applyMerge } = await this.pricingStore.updateInvoice(updateData, this.invoiceId()!, {
            deferMerge: true,
        });

        if (error) {
            this.saveError.set(S25Util.errorText(error, S25Const.defaultErrorMessage));
            return;
        }

        this.applyMerge = applyMerge;
        this.saveSuccess.set(true);
    }

    private buildUpdateData(adjustment: {
        orgId: number | undefined;
        rateScheduleId: number | undefined;
        rateGroupId: number | undefined;
        adjustmentPct: number | undefined;
        adjustmentAmt: number | undefined;
        reason: string;
    }): Partial<InvoiceUpdateData> {
        const { orgId, rateScheduleId, rateGroupId, adjustmentPct, adjustmentAmt, reason } = adjustment;
        const target = this.adjustmentTarget();

        switch (target) {
            case "lineItem": {
                const lineItem = this.lineItem()!;
                return {
                    lineItems: [
                        {
                            billId: this.invoiceId(),
                            chargeToId: orgId,
                            itemId: lineItem.itemId,
                            itemType: lineItem.itemType,
                            profileId: lineItem.profileId,
                            // The placeholder option is not a rate the server knows, so an item
                            // priced without one sends no rate at all.
                            ...(rateScheduleId !== NOTHING_CHOSEN && { rateId: rateScheduleId }),
                            ...(rateGroupId !== NOTHING_CHOSEN && { rateGroupId }),
                            adjustmentPct,
                            adjustmentAmt,
                            adjustmentName: reason,
                        },
                    ],
                };
            }
            case "occurrence": {
                const occurrence = this.occurrence()!;
                return {
                    op: this.hasAdjustment(occurrence) ? "update" : "add",
                    lineItems: [
                        {
                            billId: this.invoiceId(),
                            rsrvId: occurrence.rsrvId,
                            itemId: occurrence.itemId,
                            itemType: occurrence.itemType,
                            profileId: occurrence.profileId,
                            adjustmentPct,
                            adjustmentAmt,
                            adjustmentName: reason,
                        },
                    ],
                };
            }
            case "org": {
                const existing = this.adjustment();
                return {
                    op: existing ? "update" : "add",
                    adjustments: [
                        {
                            billId: this.invoiceId(),
                            ...(S25Util.isDefined(existing?.itemId) && { itemId: existing!.itemId }),
                            chargeToId: orgId,
                            adjustmentPct,
                            adjustmentAmt,
                            adjustmentName: reason,
                        },
                    ],
                };
            }
            default:
                S25Assert.never(target, "Unknown adjustment target");
        }
    }

    @Bind
    promptDelete() {
        this.confirmingDelete.set(true);
    }

    cancelDelete() {
        this.confirmingDelete.set(false);
    }

    @Bind
    confirmDelete() {
        this.confirmingDelete.set(false);
        this.adjustmentModel.update((prev) => ({ ...prev, adjustmentValue: 0 }));
        return this.save();
    }
}
